import React, { memo, useEffect, useRef } from 'react';
import { Box, Chip, CircularProgress, Skeleton, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import '@fontsource/rubik';
import KanbanCard from './KanbanCard';
import { useKanbanColumnData } from '../hooks/useKanbanColumnData';

const SKELETON_COUNT = 3;

const SkeletonCard = ({ stageColor }) => (
  <Box
    sx={{
      backgroundColor: '#fff',
      borderRadius: '10px',
      p: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '9px',
      height: { xs: '189px', lg: '204px', xl: '254px' },
      flexShrink: 0,
      borderLeft: `3px solid ${stageColor}40`,
    }}
  >
    <Skeleton variant="rounded" height="10px" width="45%" animation="pulse" sx={{ borderRadius: '6px' }} />
    <Skeleton variant="rounded" height="14px" width="80%" animation="pulse" sx={{ borderRadius: '6px' }} />
    <Skeleton variant="rounded" height="10px" width="55%" animation="pulse" sx={{ borderRadius: '6px' }} />
    <Skeleton variant="rounded" height="10px" width="40%" animation="pulse" sx={{ borderRadius: '6px' }} />
  </Box>
);

const KanbanColumn = ({
  stageId,
  stageKey,
  stageLabel,
  stageColor,
  summary,
  quickTurnLookup,
  orgId,
  name,
  isLoading,
  onCardClick,
  dueWithinHours,
  editorId,
  eventTypeId,
  quickTurn,
  hiddenOnly,
  hidden = false,
}) => {
  const total = summary?.total ?? 0;

  const {
    events: fullEvents,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading: isColumnLoading,
  } = useKanbanColumnData({ stageKey, orgId, name, editorId, eventTypeId, dueWithinHours, quickTurn, hidden: hiddenOnly, enabled: true });

  const visibleCards = isColumnLoading ? (summary?.events ?? []) : fullEvents;

  // Scroll-based pagination: attach once, read live values via refs.
  const scrollContainerRef = useRef(null);
  const hasNextPageRef = useRef(hasNextPage);
  hasNextPageRef.current = hasNextPage;
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  isFetchingNextPageRef.current = isFetchingNextPage;
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - scrollTop - clientHeight < 100 &&
        hasNextPageRef.current &&
        !isFetchingNextPageRef.current
      ) {
        fetchNextPageRef.current();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []); // attached once — live values read via refs

  return (
    <Box
      data-testid={`kanban-column-${stageId}`}
      aria-label={`${stageLabel} column`}
      sx={{
        // Below 600px only the tab-selected column is shown (see KanbanBoard.js) — hidden
        // columns keep fetching/paginating in the background so switching tabs back is instant.
        display: hidden ? 'none' : 'flex',
        flexDirection: 'column',
        // Equal-width columns that grow to fill the board with no leftover space; the
        // minWidth floor kicks in the horizontal scroll (see KanbanBoard.js) once the
        // viewport is too narrow for all 5 columns at that width. On mobile a single
        // column fills the full width instead of the old "peek next column" 85vw.
        // flexShrink:0 at xs matters when the "All" tab stacks every column at once —
        // each keeps its full height (100% of the bounded board area) instead of being
        // squeezed to fit all 5 into one screen; the board area then scrolls to reveal
        // the next column, while each column's own card list still scrolls internally.
        flex: { xs: '1 0 auto', sm: '1 0 0px' },
        minWidth: { xs: '100%', sm: '260px', md: '265px', lg: '275px', xl: '290px' },
        height: '100%',
        backgroundColor: '#F8F9FB',
        borderRadius: '14px',
        border: '1px solid #E8EAF0',
        overflow: 'hidden',
      }}
    >
      {/* Column header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '2px solid #00D744',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: stageColor,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '12px', sm: '12.5px', lg: '13px', xl: '14px' },
              fontWeight: 600,
              fontFamily: 'Rubik',
              color: '#1E1E2D',
              letterSpacing: '-0.01em',
            }}
          >
            {stageLabel}
          </Typography>
        </Box>
        <Chip
          label={isLoading ? '–' : total}
          size="small"
          sx={{
            fontSize: { xs: '10px', lg: '11px' },
            fontFamily: 'Rubik',
            height: '22px',
            minWidth: '28px',
            fontWeight: 600,
            backgroundColor: '#00D744',
            color: '#08231B',
            borderRadius: '6px',
            '& .MuiChip-label': { px: '8px' },
          }}
        />
      </Box>

      {/* Scrollable card list */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.25,
          pt: 1,
          pb: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minHeight: '100px',
          scrollbarWidth: 'thin',
          scrollbarColor: `rgba(0,0,0,0.12) transparent`,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.12)',
            borderRadius: '4px',
          },
        }}
      >
        {isLoading || isColumnLoading ? (
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <SkeletonCard key={i} stageColor={stageColor} />
          ))
        ) : visibleCards.length > 0 ? (
          <>
            {visibleCards.map((rawProject) => {
              // board_column/board_summary don't return quick_turn info — merge it in
              // from the /api/v1/events-backed lookup (see useQuickTurnLookup).
              const project = { ...rawProject, ...(quickTurnLookup?.[rawProject.id] ?? {}) };
              return (
                <KanbanCard
                  key={project.id}
                  project={project}
                  stageColor={stageColor}
                  stageLabel={stageLabel}
                  onClick={() => onCardClick?.(project)}
                />
              );
            })}
            {isFetchingNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <CircularProgress size={18} sx={{ color: stageColor }} />
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 5,
              gap: 1,
              flex: 1,
            }}
          >
            <InboxOutlinedIcon sx={{ fontSize: '28px', color: '#D0D3DB' }} />
            <Typography
              sx={{
                fontSize: { xs: '11px', lg: '12px' },
                fontFamily: 'Rubik',
                color: '#B0B3BC',
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              {hiddenOnly ? 'No hidden projects' : 'No projects'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default memo(KanbanColumn);
